const dash = (value) =>
  value === null || value === undefined || value === '' ? '-' : value;

const formatEndDate = (value) => {
  if (!value) return '-';

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '-';

  const day = String(date.getUTCDate()).padStart(2, '0');
  const month = String(date.getUTCMonth() + 1).padStart(2, '0');
  return `${day}/${month}/${date.getUTCFullYear()}`;
};

const getEmployeeCoverage = (record) => record?.employeeCoverage || {};

const getPolicy = (employeeCoverage) =>
  employeeCoverage.coverageGroupCompany?.policyGroupCompany?.policy || {};

const getGroup = (employeeCoverage) =>
  employeeCoverage.coverageGroupCompany?.policyGroupCompany?.groupCompany || {};

/**
 * Convert the canonical EmployeeCoverageExclusion staging response to display
 * rows; missing values remain honest dashes instead of being inferred.
 */
export const mapCoverageExclusionsToDisplay = (records) => {
  const rows = Array.isArray(records) ? records : [];

  return rows.map((record) => {
    const employeeCoverage = getEmployeeCoverage(record);
    const policy = getPolicy(employeeCoverage);
    const group = getGroup(employeeCoverage);
    const coverage = employeeCoverage.coverageGroupCompany?.coverage || {};

    return {
      id: record.id,
      employeeCoverageId: record.employeeCoverageId,
      coverageName: dash(coverage.name),
      policyNumber: dash(policy.numberPolicy),
      groupName: dash(group.name),
      endDate: formatEndDate(record.endDate)
    };
  });
};

/**
 * Flatten the current dependent-cascade preview into table rows. The preview
 * intentionally describes current active relationships only: staging GET does
 * not provide historical dependent exclusion rows.
 */
export const mapCurrentDependentPreviewToDisplay = (preview) =>
  (Array.isArray(preview) ? preview : []).flatMap((item) => {
    const dependents = Array.isArray(item?.dependents) ? item.dependents : [];

    return dependents.map((dependent, index) => ({
      id: `${item.employeeCoverageId}-${dependent.id ?? index}`,
      dependentName: dash(dependent.fullName || dependent.rut),
      coverageName: item.coverageName,
      policyNumber: item.policyNumber,
      groupName: item.groupName
    }));
  });

/**
 * Build current dependent-cascade preview. This intentionally describes
 * current active relationships only: staging GET does not provide historical
 * dependent exclusion rows.
 */
export const buildCurrentDependentPreview = (records, activeCoverages) => {
  const displayRows = mapCoverageExclusionsToDisplay(records);
  const activeRows = Array.isArray(activeCoverages) ? activeCoverages : [];
  const activeById = new Map(
    activeRows.map((coverage) => [
      Number(coverage.id ?? coverage.employeeCoverageId),
      coverage
    ])
  );

  return displayRows.map((row) => {
    const active = activeById.get(Number(row.employeeCoverageId));
    const dependents = Array.isArray(active?.dependents)
      ? active.dependents.filter(
          (dependent) =>
            !dependent?.status ||
            dependent.status === 'ACTIVE' ||
            dependent.status === 'active'
        )
      : [];

    return {
      ...row,
      dependents,
      status: active
        ? 'Actualmente asociado a esta cobertura'
        : 'Sin datos actuales de cargas para esta cobertura'
    };
  });
};

// Backend message (400) for an employee without active coverages. It is not a
// failure: there are simply no dependents to preview.
export const NO_ACTIVE_COVERAGES_MESSAGE =
  'El empleado no tiene coberturas activas';

export const isNoActiveCoveragesError = (error) =>
  error?.response?.status === 400 &&
  error?.response?.data?.message === NO_ACTIVE_COVERAGES_MESSAGE;
